var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import Modal from '@rad/components/ui/modal';
import ActionButton from '@rad/components/ui/action-button';
import { MAX_ZOOM } from '../../../types';
// The gallery's frame. Other frames crop around the same focal point, so a choice made here holds
// for them too.
const FRAME_ASPECT = 4 / 3;
const KEY_STEP = 5;
const ZOOM_STEP = 10;
const clamp = (n) => Math.round(Math.min(100, Math.max(0, n)));
const clampZoom = (n) => Math.min(MAX_ZOOM, Math.max(100, n));
/** Drag (or arrow-key) a photo inside the gallery's frame to choose which part of it shows, and zoom
 *  in with the slider, the mouse wheel or a trackpad pinch. The photo itself is not changed: only
 *  the point the frame keeps in view and how far in it shows it. */
const PhotoFocusPositioner = (props) => {
    const [crop, setCrop] = useState({ x: props.focusX, y: props.focusY, zoom: props.zoom });
    const frameRef = useRef(null);
    const imageRef = useRef(null);
    const dragRef = useRef(null);
    const zoom = Math.round(crop.zoom);
    useEffect(() => {
        setCrop({ x: props.focusX, y: props.focusY, zoom: props.zoom });
    }, [props.focusX, props.focusY, props.zoom, props.isOpen]);
    // React listens for wheel events passively, so only a listener of our own can keep the wheel
    // from scrolling the page while it zooms the photo. A pinch arrives as a wheel event with ctrlKey.
    useEffect(() => {
        const frame = frameRef.current;
        if (frame === null || !props.canZoom)
            return;
        const handleWheel = (e) => {
            e.preventDefault();
            const pixels = e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 33 : e.deltaY;
            const step = e.ctrlKey ? -pixels : -pixels / 10;
            setCrop(prev => (Object.assign(Object.assign({}, prev), { zoom: clampZoom(prev.zoom + step) })));
        };
        frame.addEventListener('wheel', handleWheel, { passive: false });
        return () => frame.removeEventListener('wheel', handleWheel);
    }, [props.canZoom]);
    const handlePointerDown = (e) => {
        dragRef.current = { startX: e.clientX, startY: e.clientY, start: crop };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    // Dragging the photo right brings its left side into view, so the focal point moves left. Moving
    // the focal point from 0% to 100% slides the photo by as much as it overflows the frame, so the
    // photo follows the pointer at any zoom.
    const handlePointerMove = (e) => {
        const drag = dragRef.current;
        const frame = frameRef.current;
        const image = imageRef.current;
        if (drag === null || frame === null || image === null)
            return;
        const width = frame.clientWidth;
        const height = frame.clientHeight;
        const fill = image.naturalWidth > 0 && image.naturalHeight > 0
            ? Math.max(width / image.naturalWidth, height / image.naturalHeight) * (drag.start.zoom / 100)
            : 0;
        const overflowX = image.naturalWidth * fill - width;
        const overflowY = image.naturalHeight * fill - height;
        setCrop(Object.assign(Object.assign({}, drag.start), { x: overflowX > 0 ? clamp(drag.start.x - ((e.clientX - drag.startX) / overflowX) * 100) : drag.start.x, y: overflowY > 0 ? clamp(drag.start.y - ((e.clientY - drag.startY) / overflowY) * 100) : drag.start.y }));
    };
    const handlePointerUp = (e) => {
        dragRef.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
    };
    const handleKeyDown = (e) => {
        const zooms = { '+': ZOOM_STEP, '=': ZOOM_STEP, '-': -ZOOM_STEP };
        const zoomBy = zooms[e.key];
        if (zoomBy !== undefined && props.canZoom) {
            e.preventDefault();
            setCrop(prev => (Object.assign(Object.assign({}, prev), { zoom: clampZoom(Math.round(prev.zoom) + zoomBy) })));
            return;
        }
        const moves = {
            ArrowLeft: { x: -KEY_STEP, y: 0 },
            ArrowRight: { x: KEY_STEP, y: 0 },
            ArrowUp: { x: 0, y: -KEY_STEP },
            ArrowDown: { x: 0, y: KEY_STEP },
        };
        const move = moves[e.key];
        if (move === undefined)
            return;
        e.preventDefault();
        setCrop(prev => (Object.assign(Object.assign({}, prev), { x: clamp(prev.x + move.x), y: clamp(prev.y + move.y) })));
    };
    const footer = (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "btn btn-outline-secondary me-auto", onClick: () => setCrop({ x: 50, y: 50, zoom: 100 }), children: [_jsx("i", { className: "bi bi-bullseye me-1" }), "Center"] }), _jsx(ActionButton, { className: "btn btn-primary", onClick: () => __awaiter(void 0, void 0, void 0, function* () { yield props.onSave({ focusX: crop.x, focusY: crop.y, zoom }); }), children: "Save Crop" })] }));
    return (_jsxs(Modal, { isOpen: props.isOpen, title: "Adjust Photo Crop", size: "lg", onClose: props.onClose, footer: footer, children: [_jsxs("p", { className: "text-muted small mb-3", children: ["Drag the photo, or use the arrow keys, to choose which part shows in the gallery", props.canZoom ? ', and zoom in with the slider or your mouse wheel' : '', ". Your original photo is kept as uploaded."] }), _jsx("div", { ref: frameRef, role: "group", tabIndex: 0, "aria-label": `Photo crop position, ${crop.x}% from the left and ${crop.y}% from the top, at ${zoom}% zoom. Use the arrow keys to move it${props.canZoom ? ' and plus or minus to zoom' : ''}.`, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: handlePointerUp, onKeyDown: handleKeyDown, className: "position-relative overflow-hidden rounded mx-auto", style: { width: '100%', maxWidth: 560, aspectRatio: `${FRAME_ASPECT}`, cursor: 'grab', touchAction: 'none', userSelect: 'none', backgroundColor: '#e9ecef' }, children: _jsx("img", { ref: imageRef, src: props.imageUrl, alt: "", draggable: false, style: {
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: `${crop.x}% ${crop.y}%`,
                        transform: `scale(${crop.zoom / 100})`,
                        transformOrigin: `${crop.x}% ${crop.y}%`,
                        pointerEvents: 'none',
                    } }) }), _jsxs("div", { className: "d-flex align-items-center gap-2 mx-auto mt-3", style: { maxWidth: 560 }, children: [_jsx("i", { className: "bi bi-zoom-out text-muted", "aria-hidden": "true" }), _jsx("input", { type: "range", className: "form-range flex-grow-1", "aria-label": "Zoom", "aria-valuetext": `${zoom}%`, min: 100, max: MAX_ZOOM, step: 5, value: zoom, disabled: !props.canZoom, onChange: e => setCrop(prev => (Object.assign(Object.assign({}, prev), { zoom: Number(e.target.value) }))) }), _jsx("i", { className: "bi bi-zoom-in text-muted", "aria-hidden": "true" }), _jsxs("span", { className: "small text-muted text-end", style: { minWidth: '3.5em' }, children: [zoom, "%"] })] }), !props.canZoom && _jsx("p", { className: "text-muted small text-center mt-2 mb-0", children: "Save the property to zoom in on this photo." })] }));
};
export default PhotoFocusPositioner;
